import { API } from "@swivl-mono/api-helper";
import { CallRecordingResponse } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant, FormFieldWidth, Input, SearchInput } from "@swivl-mono/ui";
import React, { useEffect, useMemo, useState } from "react";

import useDebounce from "../../../Hooks/useDebounce";
import { useUrlState } from "../../../Hooks/useUrlState";
import CallRecordingsModel from "../../../Models/CallRecordings/CallRecordings.Model";
import RecordingPanel from "./Components/RecordingPanel/RecordingPanel";
import RecordingsDataTable from "./Components/RecordingsDataTable/RecordingsDataTable";
import { downloadMultipleFiles } from "./utils/downloadFile";

const SEARCH_DEBOUNCE_MS = 300;

const RecordingsView = () => {
  const [selectedRecordingSids, setSelectedRecordingSids] = useState<string[]>([]);

  const { searchParams, setSearchParam, setMultipleSearchParams } = useUrlState();
  const recordingId = searchParams.get("recordingId");
  const searchQuery = searchParams.get("search") ?? "";
  const userIdParam = searchParams.get("userId");

  const { debouncedValue: debouncedSearchQuery } = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);

  const { currentData, error } = CallRecordingsModel.useState((state) => ({
    currentData: state.currentData,
    isLoading: state.isLoading,
    error: state.error
  }));

  const [deepLinkedRecording, setDeepLinkedRecording] = useState<CallRecordingResponse | null>(null);

  // Sync initial URL params to model and load on mount; reset userId filter on unmount
  useEffect(() => {
    const parsedUserId = userIdParam !== null ? Number(userIdParam) : undefined;
    const userId = parsedUserId !== undefined && Number.isInteger(parsedUserId) ? parsedUserId : undefined;
    CallRecordingsModel.setState({ search: searchQuery, userId });
    void CallRecordingsModel.load();
    return () => {
      CallRecordingsModel.setState({ userId: undefined });
    };
  }, []);

  useEffect(() => {
    void CallRecordingsModel.setSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  // When recordingId is in the URL but not on the current page, fetch it directly
  useEffect(() => {
    if (!recordingId) {
      setDeepLinkedRecording(null);
      return;
    }
    const found = currentData.find((item) => item.callSid === recordingId);
    if (found) {
      setDeepLinkedRecording(null);
      return;
    }
    let cancelled = false;
    void API.Calls.getCallRecordingByCallSid(recordingId)
      .then((data) => {
        if (!cancelled) setDeepLinkedRecording(data ?? null);
      })
      .catch(() => {
        if (!cancelled) setDeepLinkedRecording(null);
      });
    return () => {
      cancelled = true;
    };
  }, [recordingId, currentData]);

  const selectedRecording: CallRecordingResponse | null = useMemo(
    () => currentData.find((item) => item.callSid === recordingId) || deepLinkedRecording || null,
    [currentData, recordingId, deepLinkedRecording]
  );

  const handleSelectCell = (id: string) => {
    setSearchParam("recordingId", id);
  };

  const handleClosePanel = () => {
    setSearchParam("recordingId", undefined);
  };

  const handleDownloadSelectedAudio = async () => {
    if (selectedRecordingSids.length === 0) {
      return;
    }

    const itemsToDownload = selectedRecordingSids
      .map((sid) => currentData.find((item) => item.callSid === sid))
      .filter((item): item is CallRecordingResponse => Boolean(item && item.recordingUrl))
      .map((item) => ({
        url: item.recordingUrl as string,
        fileName: `call-${item.callSid}.wav`
      }));

    if (itemsToDownload.length === 0) {
      return;
    }

    await downloadMultipleFiles(itemsToDownload);
  };

  if (error) {
    return (
      <div className="flex h-full items-center justify-center bg-grey-50">
        <div className="text-center">
          <p className="text-lg font-semibold text-red-600">Error loading recordings</p>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-grey-50">
      <div className="border-b border-gray-200 px-4 py-3">
        {selectedRecordingSids.length > 0 ? (
          <div className="flex items-center gap-3">
            <Button variant={ButtonVariant.PRIMARY} color={ButtonColor.BLACK} size={ButtonSize.MEDIUM} onClick={handleDownloadSelectedAudio}>
              Download Audio
            </Button>
            <Button variant={ButtonVariant.PRIMARY} color={ButtonColor.BLACK} size={ButtonSize.MEDIUM} disabled>
              Download Transcript
            </Button>
            <span className="text-sm font-medium text-gray-900">
              <span className="font-semibold">{selectedRecordingSids.length}</span>
              <span> Items Selected</span>
            </span>
          </div>
        ) : (
          <SearchInput
            width={FormFieldWidth.RESPONSIVE_240}
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => {
              if (userIdParam) {
                setMultipleSearchParams({ search: e.target.value || undefined, userId: undefined });
                CallRecordingsModel.setState({ userId: undefined });
              } else {
                setSearchParam("search", e.target.value || undefined);
              }
            }}
            onClear={() => {
              if (userIdParam) {
                setMultipleSearchParams({ search: undefined, userId: undefined });
                CallRecordingsModel.setState({ search: "", userId: undefined, page: 1, totalPages: 1, total: 0 });
                void CallRecordingsModel.load(true);
              } else {
                setSearchParam("search", undefined);
              }
            }}
          />
        )}
      </div>

      <div className="flex-1 overflow-auto overscroll-none">
        <RecordingsDataTable
          data={currentData}
          handleSelectCell={handleSelectCell}
          onSelectedIdsChange={setSelectedRecordingSids}
          selectedRowId={recordingId}
        />
      </div>
      {selectedRecording && <RecordingPanel recording={selectedRecording} onClose={handleClosePanel} />}
    </div>
  );
};

export default RecordingsView;
